import { ArrowRightLeft, ChevronRight, RefreshCw, Search, TriangleAlert, CircleX, Info } from 'lucide-react';
import { useId, useState, type Ref } from 'react';
import type { NetworkImpact } from '../../api/types';
import { strings } from '../../strings';
import { cloudflareCostLine } from '../entryPoints/helpers';

const t = strings.networks.topology;

const reach = (value: string) => t.reachability[value] ?? value;

/**
 * The impact panel of a topology or firewall change (spec 11 6.15.12, U1.3, U1.10, mock n04), from the dry run's
 * `impact` in spec 18 5.7's order: the summary line (the polite live region), lost reachability (every pair, searchable,
 * with its pending requests and View requests), interruptions, costs and commitments, deployments to apply, changed
 * connectivity (grouped, expandable) and resources (collapsed). While validating the last result stays dimmed.
 */
export function ImpactPanel({
  impact,
  state,
  panelRef,
  onRetry,
  onViewRequests,
}: {
  impact: NetworkImpact | null;
  state: 'loading' | 'done' | 'error' | 'idle';
  panelRef?: Ref<HTMLElement>;
  onRetry: () => void;
  onViewRequests: (a: string, b: string) => void;
}) {
  const ids = useId();
  const [query, setQuery] = useState('');
  const loading = state === 'loading';
  const pairs = impact?.losesReachability ?? [];
  const q = query.trim().toLowerCase();
  const shown = q
    ? pairs.filter((x) => `${x.a.name} ${x.b.name} ${x.a.region} ${x.b.region}`.toLowerCase().includes(q))
    : pairs;
  return (
    <aside
      ref={panelRef}
      tabIndex={-1}
      aria-labelledby={`${ids}-title`}
      className="net-impact"
      data-testid="impact-panel"
    >
      <div className="net-impact-head">
        <ArrowRightLeft aria-hidden className="size-4 shrink-0" />
        <h2 id={`${ids}-title`} className="flex-1 text-[15px] font-semibold">
          {t.impact}
        </h2>
        <span className="text-[12px] text-fg-muted">{t.impactNote}</span>
      </div>
      <div className="net-impact-summary">
        {/* Announced once per answer of the dry run (after the debounce), never while it runs. */}
        <p role="status" aria-live="polite" className="font-semibold" data-testid="impact-summary">
          {state === 'error' ? t.impactUnknown : (impact?.summary ?? '')}
        </p>
        {loading && <p className="text-[12.5px] text-fg-muted">{t.updating}</p>}
        {state === 'error' && (
          <button type="button" className="link text-[13px]" onClick={onRetry}>
            <RefreshCw aria-hidden className="mr-1 inline size-3.5" />
            {strings.common.retry}
          </button>
        )}
      </div>
      {impact && (
        <div
          className={`net-impact-body ${loading || state === 'error' ? 'is-stale' : ''}`}
          aria-busy={loading || undefined}
        >
          <section aria-labelledby={`${ids}-loses`}>
            <h3 id={`${ids}-loses`} className="net-impact-heading text-danger">
              {t.losesReachability(pairs.length)}
            </h3>
            {pairs.length === 0 ? (
              <p className="text-[13px]">{t.noPairLoses}</p>
            ) : (
              <>
                <label className="net-impact-search">
                  <Search aria-hidden className="size-4 text-fg-muted" />
                  <span className="sr-only">{t.searchPairs}</span>
                  <input
                    type="search"
                    className="min-w-0 flex-1 bg-transparent outline-none"
                    placeholder={t.searchPairs}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </label>
                <ul className="net-impact-pairs" data-testid="lost-pairs">
                  {shown.map((pair) => (
                    <li
                      key={`${pair.a.deploymentId}~${pair.b.deploymentId}~${pair.a.region}~${pair.b.region}`}
                    >
                      <span className="min-w-0 flex-1 font-mono text-[12.5px]">
                        {t.pairName(pair.a.name, pair.b.name)}
                      </span>
                      {pair.pendingRequests.length > 0 ? (
                        <span className="text-[12px]">
                          {t.pendingRequests(pair.pendingRequests.length)} ·{' '}
                          <button
                            type="button"
                            className="link"
                            aria-label={t.viewRequestsFor(pair.a.name, pair.b.name)}
                            onClick={() => onViewRequests(pair.a.deploymentId, pair.b.deploymentId)}
                          >
                            {t.viewRequests}
                          </button>
                        </span>
                      ) : (
                        <span className="text-[12px] text-fg-muted">{t.noRequest}</span>
                      )}
                    </li>
                  ))}
                </ul>
                {shown.length === 0 && <p className="text-[13px] text-fg-muted">{t.noPairsMatch}</p>}
              </>
            )}
          </section>
          <section aria-labelledby={`${ids}-interruptions`}>
            <h3 id={`${ids}-interruptions`} className="net-impact-heading">
              {t.interruptions}
            </h3>
            {impact.interruptions.length === 0 ? (
              <p className="text-[13px]">{t.noInterruptions}</p>
            ) : (
              <ul className="space-y-2">
                {impact.interruptions.map((x, i) => {
                  const Icon =
                    x.severity === 'danger' ? CircleX : x.severity === 'warning' ? TriangleAlert : Info;
                  return (
                    <li key={i} className={`callout callout-${x.severity === 'info' ? 'info' : x.severity}`}>
                      <Icon aria-hidden className="size-4 shrink-0" />
                      <span>{x.text}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
          <section aria-labelledby={`${ids}-costs`}>
            <h3 id={`${ids}-costs`} className="net-impact-heading">
              {t.costs}
            </h3>
            {impact.costs.length === 0 && impact.commitments.length === 0 ? (
              <p className="text-[13px]">{t.noCostChange}</p>
            ) : (
              <ul className="space-y-1 text-[13px]" data-testid="impact-costs">
                {impact.costs.map((c, i) => (
                  <li key={i} data-provider={c.provider}>
                    <span className="font-semibold">
                      {c.provider === 'cloudflare' && c.change === 'add'
                        ? // Feature spec 17, phase 8: the zone plan the entry point relies on, per zone.
                          cloudflareCostLine(c.item, c.currency, c.monthly)
                        : t.costLine(
                            c.change === 'add' ? '+' : '−',
                            `${c.currency} ${c.monthly.toLocaleString('en-US')}`,
                            c.item,
                          )}
                    </span>
                    <span className="block text-[12px] text-fg-muted">{t.asOf(c.asOf)}</span>
                  </li>
                ))}
                {impact.commitments.map((c, i) => (
                  // Feature spec 17, phase 5: a commitment in the danger tone (spec 11 6.15.33).
                  <li key={`c${i}`} className="text-[var(--danger)]" data-testid="impact-commitment">
                    <span className="font-semibold">
                      {c.renewalOnly ? t.renewalOf(c.label) : c.label}: {c.currency}{' '}
                      {c.monthly.toLocaleString('en-US')}/month for {c.months} months
                      {c.autoRenew ? t.renewsEachTerm : ''}
                    </span>
                    <span className="block text-[12px]">
                      {c.per} · {t.asOf(c.asOf)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section aria-labelledby={`${ids}-apply`}>
            <h3 id={`${ids}-apply`} className="net-impact-heading">
              {t.deploymentsToApply}
            </h3>
            {impact.deploymentsToApply.length === 0 ? (
              <p className="text-[13px]">{t.none}</p>
            ) : (
              <ul className="space-y-1 text-[13px]">
                {impact.deploymentsToApply.map((d) => (
                  <li key={d.deploymentId}>
                    <span className="font-mono font-semibold">{d.name}</span> · {d.reason}
                  </li>
                ))}
              </ul>
            )}
          </section>
          {impact.changedConnectivity.length > 0 && (
            <details className="net-impact-details">
              <summary>
                <ChevronRight aria-hidden className="net-impact-chevron size-4" />
                {t.changedConnectivity(impact.changedConnectivity.length)}
              </summary>
              <ul className="mt-1 space-y-1 text-[13px]">
                {impact.changedConnectivity.map((g, i) => (
                  <li key={i}>
                    <details>
                      <summary className="cursor-pointer">
                        {t.changeGroup(reach(g.before), reach(g.after), g.count)}
                      </summary>
                      <ul className="ml-4 font-mono text-[12px]">
                        {g.pairs.map((pair) => (
                          <li key={pair}>{pair}</li>
                        ))}
                      </ul>
                    </details>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {impact.resources.length > 0 && (
            <details className="net-impact-details">
              <summary>
                <ChevronRight aria-hidden className="net-impact-chevron size-4" />
                {t.resources}
              </summary>
              <ul className="mt-1 space-y-1 text-[13px]">
                {impact.resources.map((r, i) => (
                  <li key={i}>{t.resourceLine(r.kind, r.create, r.delete)}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {!impact && state !== 'error' && <p className="px-4 py-3 text-[13px] text-fg-muted">{t.updating}</p>}
    </aside>
  );
}
